// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { TableModule } from "primeng/table";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";
import * as C from "../../../../../../shared/constants/globals.constants";
import * as MSL from "src/app/shared/constants/msl.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";

@Component({
  selector: C.EWIZ_RFM_INSTANCE_CONFIGURATION,
  templateUrl: "./rfm-instance-configuration.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    RadioButtonModule,
    TableModule,
    SharedDirectivesModule,
    ErrorMessageComponent
  ]
})
export class EWizRFMInstanceConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck {
  constructor(private dataService: DataService) {
    super();
  }

  selectedInstances: ProfileModel.RFMConfigurationDto[] | null = null;
  nextId = 0;
  nextTarId = 0;
  readonly expandLabel = $localize`Expand RFM Instance`;
  readonly collapseLabel = $localize`Collapse RFM Instance`;

  ngOnInit() {
    this.errorCheck(this.profileValues, this.dataService);

    if (this.profileValues.rfmConfiguration.length > 0) {
      this.nextId = +this.profileValues.rfmConfiguration[this.profileValues.rfmConfiguration.length - 1].id;
    }

    for (const configuration of this.profileValues.rfmConfiguration) {
      for (const tar of configuration.tarList.tars) {
        if (tar.tarId > this.nextTarId) {
          this.nextTarId = tar.tarId;
        }
      }
    }
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    if (values.rfmConfiguration.length > 0) {
      for (let i = 0; i < values.rfmConfiguration.length; i++) {
        for (let ii = 0; ii < values.rfmConfiguration.length; ii++) {
          if (i !== ii) {
            if (
              !dataService.isNullOrEmpty(values.rfmConfiguration[i].instanceAID) &&
              dataService.validHexValue(values.rfmConfiguration[i].instanceAID, 5, 16) &&
              !dataService.isNullOrEmpty(values.rfmConfiguration[ii].instanceAID) &&
              dataService.validHexValue(values.rfmConfiguration[ii].instanceAID, 5, 16)
            ) {
              if (values.rfmConfiguration[i].instanceAID.toLowerCase() === values.rfmConfiguration[ii].instanceAID.toLowerCase()) {
                this.errorMessage = $localize`Instance AID values must be unique`;
                this.errorFound = true;
              }
            }
          }
        }
      }

      const tarValues = [];

      // Push all TAR values from RAM configuration to ensure TAR values entered here are distinct
      if (values.ramConfiguration.tarList.tars.length > 0) {
        for (const tar of values.ramConfiguration.tarList.tars) {
          if (!dataService.isNullOrEmpty(tar.tarValue) && dataService.validHexValue(tar.tarValue, 3, 3)) {
            if (!tarValues.includes(tar.tarValue.toLowerCase())) {
              tarValues.push(tar.tarValue.toLowerCase());
            }
          }
        }
      }

      for (const configuration of values.rfmConfiguration) {
        // Each RFM instance must have an AID value entered and valid
        if (dataService.isNullOrEmpty(configuration.instanceAID)) {
          this.errorMessage = $localize`Instance AID values must be entered`;
          this.errorFound = true;
          break;
        } else if (!dataService.validHexValue(configuration.instanceAID, 5, 16)) {
          this.errorMessage = $localize`Instance AID values must be between 5 and 16 HEX bytes in length`;
          this.errorFound = true;
          break;
        }

        if (configuration.tarList.tars.length === 0) {
          this.errorMessage = $localize`Each RFM Instance must have at least one TAR Value `;
          this.errorFound = true;
          break;
        } else {
          for (const tar of configuration.tarList.tars) {
            if (dataService.isNullOrEmpty(tar.tarValue)) {
              this.errorMessage = $localize`TAR values must be entered`;
              this.errorFound = true;
              break;
            } else if (!dataService.validHexValue(tar.tarValue, 3, 3)) {
              this.errorMessage = $localize`TAR values must be 3 HEX bytes in length`;
              this.errorFound = true;
              break;
            } else {
              if (tarValues.includes(tar.tarValue.toLowerCase())) {
                this.errorMessage = $localize`TAR values must be unique across RFM and RAM configuration`;
                this.errorFound = true;
                break;
              } else {
                tarValues.push(tar.tarValue.toLowerCase());
              }
            }
          }
        }

        // Each RFM instance must have a MSL value entered and valid
        if (dataService.isNullOrEmpty(configuration.mslValue)) {
          this.errorMessage = $localize`MSL values must be entered`;
          this.errorFound = true;
          break;
        } else if (!dataService.validHexValue(configuration.mslValue, 1, 1)) {
          this.errorMessage = $localize`MSL values must be 1 HEX byte in length`;
          this.errorFound = true;
          break;
        } else {
          let mslValueValid = false;

          for (const value of MSL.MSL_VALUE_MATRIX) {
            if (value[MSL.MSL_HEX_CODE].toUpperCase() === configuration.mslValue.toUpperCase()) {
              mslValueValid = true;
              break;
            }
          }

          if (!mslValueValid) {
            if (parseInt(configuration.mslValue, 16) >= 32) {
              this.errorMessage = $localize`MSL value is invalid, values of 20 and above are RFU`;
              this.errorFound = true;
            } else {
              this.errorMessage = $localize`MSL value is invalid`;
              this.errorFound = true;
            }

            break;
          }
        }

        // Each RFM instance must have a UICC Access Domain value entered and valid
        if (dataService.isNullOrEmpty(configuration.uiccAccessDomain)) {
          this.errorMessage = $localize`UICC Access Domain values must be entered`;
          this.errorFound = true;
          break;
        } else if (!dataService.validHexValue(configuration.uiccAccessDomain, 1, 1)) {
          this.errorMessage = $localize`UICC Access Domain values must be 1 HEX byte in length`;
          this.errorFound = true;
          break;
        }

        // Each RFM instance must have a UICC Admin Access Domain value entered and valid
        if (dataService.isNullOrEmpty(configuration.uiccAdminAccessDomain)) {
          this.errorMessage = $localize`UICC Admin Access Domain values must be entered`;
          this.errorFound = true;
          break;
        } else if (!dataService.validHexValue(configuration.uiccAdminAccessDomain, 1, 1)) {
          this.errorMessage = $localize`UICC Admin Access Domain values must be 1 HEX byte in length`;
          this.errorFound = true;
          break;
        }

        // Each RFM instance must have a ADF AID value entered and valid
        if (!dataService.isNullOrEmpty(configuration.adfAID) && !dataService.validHexValue(configuration.adfAID, 5, 16)) {
          this.errorMessage = $localize`ADF AID values must be between 5 and 16 HEX bytes in length`;
          this.errorFound = true;
          break;
        }

        // Each RFM instance must have a ADF Access Domain value entered and valid
        if (!dataService.isNullOrEmpty(configuration.adfAID)) {
          if (dataService.isNullOrEmpty(configuration.adfAccessDomain)) {
            this.errorMessage = $localize`ADF Access Domain values must be entered`;
            this.errorFound = true;
            break;
          } else if (!dataService.validHexValue(configuration.adfAccessDomain, 1, 1)) {
            this.errorMessage = $localize`ADF Access Domain values must be 1 HEX byte in length`;
            this.errorFound = true;
            break;
          }
        }

        // Each RFM instance must have a ADF Admin Access Domain value entered and valid
        if (!dataService.isNullOrEmpty(configuration.adfAID)) {
          if (dataService.isNullOrEmpty(configuration.adfAdminAccessDomain)) {
            this.errorMessage = $localize`ADF Admin Access Domain values must be entered`;
            this.errorFound = true;
            break;
          } else if (!dataService.validHexValue(configuration.adfAdminAccessDomain, 1, 1)) {
            this.errorMessage = $localize`ADF Admin Access Domain values must be 1 HEX byte in length`;
            this.errorFound = true;
            break;
          }
        }
      }
    }

    return this.errorFound;
  }

  addInstance() {
    this.profileValues.rfmConfiguration.push({
      id: ++this.nextId,
      instanceAID: null,
      tarList: {
        tars: [],
        selectedTars: null
      },
      mslValue: null,
      integrityCheckOption: null,
      cipheringOption: null,
      counterOption: null,
      uiccAccessDomain: null,
      uiccAccessDomainRadioValue: null,
      uiccAdminAccessDomain: null,
      uiccAdminAccessDomainRadioValue: null,
      adfAID: null,
      adfAccessDomain: null,
      adfAccessDomainRadioValue: null,
      adfAdminAccessDomain: null,
      adfAdminAccessDomainRadioValue: null
    });

    this.handleFormEvents();

    setTimeout(() => {
      const rfmElements = document.getElementsByClassName("rfm-expanel-toggler");

      if (rfmElements) {
        (rfmElements[rfmElements.length - 1] as HTMLButtonElement).click();
      }
    });
  }

  deleteInstance() {
    const newInstances = [];

    for (const originalInstance of this.profileValues.rfmConfiguration) {
      let removeInstance = false;

      for (const selectedParameter of this.selectedInstances) {
        if (originalInstance.id === selectedParameter.id) {
          removeInstance = true;
          break;
        }
      }

      if (!removeInstance) {
        newInstances.push(originalInstance);
      }
    }

    this.profileValues.rfmConfiguration = [...newInstances];
    this.selectedInstances = [];

    this.handleFormEvents();
  }

  addTar(instance: ProfileModel.RFMConfigurationDto) {
    instance.tarList.tars.push({
      tarId: ++this.nextTarId,
      tarValue: null
    });

    this.handleFormEvents();
  }

  deleteTar(instance: ProfileModel.RFMConfigurationDto) {
    const newTarList = [];

    for (const originalTar of instance.tarList.tars) {
      let removeTar = false;

      for (const selectedTar of instance.tarList.selectedTars) {
        if (originalTar.tarId === selectedTar.tarId) {
          removeTar = true;
          break;
        }
      }

      if (!removeTar) {
        newTarList.push(originalTar);
      }
    }

    instance.tarList.tars = [...newTarList];
    instance.tarList.selectedTars = [];

    this.handleFormEvents();
  }

  mslInputEventHandler(instance: ProfileModel.RFMConfigurationDto) {
    instance.integrityCheckOption = null;
    instance.cipheringOption = null;
    instance.counterOption = null;

    if (
      !this.dataService.isNullOrEmpty(instance.mslValue) &&
      this.dataService.validHexValue(instance.mslValue, 1, 1) &&
      this.dataService.hexValueInRange(instance.mslValue, 0x00, 0xff)
    ) {
      for (const value of MSL.MSL_VALUE_MATRIX) {
        if (value[MSL.MSL_HEX_CODE].toLocaleLowerCase() === instance.mslValue.toLowerCase()) {
          instance.integrityCheckOption = value[MSL.MSL_INTEGRITY_CHECK];
          instance.cipheringOption = value[MSL.MSL_CIPHERING];
          instance.counterOption = value[MSL.MSL_COUNTER];
          break;
        }
      }
    }

    this.handleFormEvents();
  }

  mslRadioEventHandler(instance: ProfileModel.RFMConfigurationDto) {
    instance.mslValue = null;

    for (const value of MSL.MSL_VALUE_MATRIX) {
      if (
        +instance.integrityCheckOption === +value[MSL.MSL_INTEGRITY_CHECK] &&
        +instance.cipheringOption === +value[MSL.MSL_CIPHERING] &&
        +instance.counterOption === +value[MSL.MSL_COUNTER]
      ) {
        instance.mslValue = `${value[MSL.MSL_HEX_CODE]}`;
        break;
      }
    }

    this.handleFormEvents();
  }

  handleInputRadioToggle(instance: ProfileModel.RFMConfigurationDto, groupName) {
    switch (groupName) {
      case "uiccAccessDomain": {
        if (parseInt(instance.uiccAccessDomain, 16) === 0x00) {
          instance.uiccAccessDomainRadioValue = "1";
        } else if (parseInt(instance.uiccAccessDomain, 16) === 0xff) {
          instance.uiccAccessDomainRadioValue = "2";
        } else {
          instance.uiccAccessDomainRadioValue = null;
        }
        break;
      }
      case "uiccAdminAccessDomain": {
        if (parseInt(instance.uiccAdminAccessDomain, 16) === 0x00) {
          instance.uiccAdminAccessDomainRadioValue = "1";
        } else if (parseInt(instance.uiccAdminAccessDomain, 16) === 0xff) {
          instance.uiccAdminAccessDomainRadioValue = "2";
        } else {
          instance.uiccAdminAccessDomainRadioValue = null;
        }
        break;
      }
      case "adfAccessDomain": {
        if (parseInt(instance.adfAccessDomain, 16) === 0x00) {
          instance.adfAccessDomainRadioValue = "1";
        } else if (parseInt(instance.adfAccessDomain, 16) === 0xff) {
          instance.adfAccessDomainRadioValue = "2";
        } else {
          instance.adfAccessDomainRadioValue = null;
        }
        break;
      }
      case "adfAdminAccessDomain": {
        if (parseInt(instance.adfAdminAccessDomain, 16) === 0x00) {
          instance.adfAdminAccessDomainRadioValue = "1";
        } else if (parseInt(instance.adfAdminAccessDomain, 16) === 0xff) {
          instance.adfAdminAccessDomainRadioValue = "2";
        } else {
          instance.adfAdminAccessDomainRadioValue = null;
        }
        break;
      }
    }

    this.handleFormEvents();
  }
}
